import React, { useEffect, useState } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import * as Yup from "yup";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { studentShortQuestionAPI } from "../../redux/features/courseSlice";
import AssignmentTimerCompleteModal from "../assignmentTimerCompleteModal/AssignmentTimerCompleteModal";
import AssignmentConfirmSubmitModal from "../AssignmentConfirmSubmitModal/AssignmentConfirmSubmitModal";

const ShortQuestionAssignment = ({ data }) => {
  const [questions, setQuestions] = useState([]);
  const [counter, setCounter] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [fieldDisabled, setFieldDisabled] = useState(false);

  // Validation schema
  const validationSchema = Yup.object().shape({
    assignmentId: Yup.string().required("Assignment ID is required"),
    marksArray: Yup.array().of(
      Yup.object().shape({
        question_id: Yup.string().required("Question ID is required"),
        user_answer: Yup.string(),
      })
    ),
  });

  useEffect(() => {
    setQuestions(data?.questions || []);
  }, [data]);

  useEffect(() => {
    if (data?.duration) {
      setCounter(data.duration * 60);
      setTimerStarted(true);
    } else {
      setTimerStarted(false);
    }
  }, [data?.duration]);

  const FormSubmitter = () => {
    const { submitForm } = useFormikContext();

    useEffect(() => {
      if (searchParams.get("view") === "true" || !timerStarted) {
        return;
      }

      const interval = setInterval(() => {
        setCounter((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            if (!formSubmitted) {
              submitForm().then(() => {});
              setShowTimerModal(true);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }, [submitForm]);

    return null;
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")} min`;
  };

  const initialMarksArray =
    data?.questions?.map((question) => ({
      question_id: question?.id || "",
      user_answer: "",
    })) || [];

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const res = await dispatch(
        studentShortQuestionAPI({
          token: auth.token,
          assignment_Id: searchParams.get("id"),
          data: values.marksArray,
        })
      ).unwrap();
      if (res.success) {
        toast.success(res?.message, {
          position: "top-center",
        });
        setFormSubmitted(true);
      } else {
        throw new Error(res?.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        error.message || "Failed to submit answers. Please try again.",
        {
          position: "top-center",
        }
      );
      setStatus({
        error: error.message || "Failed to submit answers. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTimerContinue = () => {
    setShowTimerModal(false);
    navigate(-1);
  };

  const handleSubmitCancel = () => {
    setShowSubmitModal(false);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    setShowSubmitModal(true);
  };

  useEffect(() => {
    if (searchParams.get("view") === "true") {
      setFieldDisabled(true);
    } else {
      setFieldDisabled(false);
    }
  }, []);

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="assignment-header mb-10 mb-sm-20 mb-md-24">
        <h3 className="fw-bold ah-heading-04">{data?.title}</h3>
        <p>{data?.description}</p>
        <div className="item ah-text-md fw-bold theme-color text-end">
          Duration:{" "}
          <span className="fw-semibold black-100 opacity-50">
            {formatTime(counter)}
          </span>
        </div>
      </div>

      <div className="card-default-style px-15 px-sm-25 px-md-35">
        <div className="assignment">
          <div
            className="assignment-inner overflow-hidden"
            style={{ maxHeight: "100%" }}
          >
            {initialMarksArray.length > 0 && (
              <Formik
                initialValues={{
                  assignmentId: searchParams.get("id") || "",
                  marksArray: initialMarksArray,
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
              >
                {({
                  values,
                  errors,
                  touched,
                  status,
                  isSubmitting,
                  submitForm,
                }) => (
                  <>
                    <Form
                      id="short-question-form"
                      onSubmit={handleManualSubmit}
                    >
                      {status?.success && (
                        <div className="alert alert-success">
                          {status.success}
                        </div>
                      )}
                      {status?.error && (
                        <div className="alert alert-danger">{status.error}</div>
                      )}

                      <ul className="p-0 list-unstyled mb-10 mb-sm-20 mb-md-32">
                        {questions.map((question, index) => (
                          <li
                            key={question.id}
                            className="mb-18 d-flex align-items-start justify-content-between flex-column-reverse flex-sm-row gap-2 gap-lg-3 gap-xl-5"
                          >
                            <div className="question w-100">
                              <Field
                                type="hidden"
                                name={`marksArray.${index}.question_id`}
                                value={question.id}
                              />
                              <label
                                htmlFor={`marksArray.${index}.user_answer`}
                                className="form-label fw-bold ah-text-md black-100 mb-14"
                              >
                                {index + 1}. {question?.question_text}
                              </label>

                              <Field
                                as="textarea"
                                className="form-control"
                                name={`marksArray.${index}.user_answer`}
                                disabled={fieldDisabled}
                                rows={3}
                                placeholder="Write Answer..."
                              />
                              {errors.marksArray &&
                                errors.marksArray[index]?.user_answer &&
                                touched.marksArray &&
                                touched.marksArray[index]?.user_answer && (
                                  <div className="text-danger d-block">
                                    {errors.marksArray[index].user_answer}
                                  </div>
                                )}
                            </div>

                            <span className="points flex-shrink-0 h-auto px-29 py-8 ah-text-xs fw-semibold">
                              {question.marks || 1} Point
                              {question.marks !== 1 ? "s" : ""}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-20">
                        {fieldDisabled ? (
                          <button
                            className="btn btn-primary no-shadow px-50 py-15"
                            type="button"
                            onClick={goBack}
                          >
                            Back
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary no-shadow px-50 py-15"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Submitting..." : "Submit"}
                          </button>
                        )}
                      </div>
                    </Form>
                    <FormSubmitter />
                    <AssignmentTimerCompleteModal
                      show={showTimerModal}
                      onContinue={handleTimerContinue}
                    />
                    <AssignmentConfirmSubmitModal
                      show={showSubmitModal}
                      onConfirm={() => {
                        setShowSubmitModal(false);
                        submitForm().then(() => {});
                        navigate(-1);
                      }}
                      onCancel={handleSubmitCancel}
                    />
                  </>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShortQuestionAssignment;